/**
 * Soft-gate AdSense script load until the consent overlay lane is free,
 * or until a timeout so LCP/TBT do not stall indefinitely.
 *
 * Starts closed (`false`) so a same-tick HybridConsentManager effect can claim
 * the consent lane before AdSense mounts.
 */
import { useEffect, useState } from 'react';
import {
  getActiveOverlayClaim,
  subscribeOverlayClaims,
} from './overlayAdCoordinator';

const DEFAULT_CONSENT_GATE_TIMEOUT_MS = 5000;

export function useAdSenseConsentGate(
  timeoutMs: number = DEFAULT_CONSENT_GATE_TIMEOUT_MS,
): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = () => {
      if (cancelled) {
        return false;
      }
      const active = getActiveOverlayClaim();
      if (!active || active.lane !== 'consent') {
        setReady(true);
        return true;
      }
      return false;
    };

    // Defer one frame so HybridConsentManager's acquire effect can run first
    // when both mount under the same deferredAdSenseReady gate.
    const raf = window.requestAnimationFrame(() => {
      if (check()) {
        return;
      }
    });

    const unsub = subscribeOverlayClaims(() => {
      check();
    });
    const timer = window.setTimeout(() => {
      if (!cancelled) {
        setReady(true);
      }
    }, timeoutMs);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      unsub();
      window.clearTimeout(timer);
    };
  }, [timeoutMs]);

  return ready;
}
